import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Edit2, Trash2, Star, Quote, Save, X, Pin } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const API = process.env.REACT_APP_BACKEND_URL;

const StarRating = ({ rating, onRate, interactive = false }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onRate && onRate(star)}
          onMouseEnter={() => interactive && setHover(star)}
          onMouseLeave={() => interactive && setHover(0)}
          className={interactive ? 'cursor-pointer' : 'cursor-default'}
        >
          <Star
            className={`w-5 h-5 ${
              star <= (hover || rating)
                ? 'fill-yellow-400 text-yellow-400'
                : 'fill-gray-200 text-gray-200'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const TestimonialForm = ({ testimonial, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: testimonial?.name || '',
    location: testimonial?.location || '',
    quote: testimonial?.quote || '',
    rating: testimonial?.rating || 5,
    featured: testimonial?.featured !== false,
    hero_highlighted: testimonial?.hero_highlighted === true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.location.trim() || !formData.quote.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border-2 border-cyan-200 p-6 mb-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">
        {testimonial ? 'Edit Testimonial' : 'Add New Testimonial'}
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Customer Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            placeholder="e.g., Sarah M."
            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
            data-testid="testimonial-name-input"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Location</label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => setFormData({...formData, location: e.target.value})}
            placeholder="e.g., New York, USA"
            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
            data-testid="testimonial-location-input"
          />
        </div>
      </div>
      
      <div className="mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-1">Quote</label>
        <textarea
          value={formData.quote}
          onChange={(e) => setFormData({...formData, quote: e.target.value})}
          placeholder="What did the customer say about their experience?"
          rows={3}
          maxLength={500}
          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 resize-none"
          data-testid="testimonial-quote-input"
        />
        <p className="text-xs text-gray-500 mt-1">{formData.quote.length}/500 characters</p>
      </div>
      
      <div className="flex flex-wrap items-center gap-6 mb-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Rating</label>
          <StarRating rating={formData.rating} onRate={(r) => setFormData({...formData, rating: r})} interactive />
        </div>
        
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.featured}
            onChange={(e) => setFormData({...formData, featured: e.target.checked})}
            className="w-5 h-5 rounded border-gray-300 text-cyan-500 focus:ring-cyan-500"
          />
          <span className="text-sm font-semibold text-gray-700">Show on Homepage</span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer" title="Pin this testimonial to the hero strip above the tour catalog. Only one testimonial can be highlighted at a time.">
          <input
            type="checkbox"
            checked={formData.hero_highlighted}
            onChange={(e) => setFormData({...formData, hero_highlighted: e.target.checked})}
            data-testid="testimonial-hero-highlight-checkbox"
            className="w-5 h-5 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
          />
          <span className="text-sm font-semibold text-gray-700 flex items-center gap-1">
            <Pin className="w-4 h-4 text-amber-500" />
            Highlight on Homepage Hero
          </span>
        </label>
      </div>
      
      <div className="flex gap-3">
        <button
          type="submit"
          disabled={saving}
          className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white py-2 rounded-lg font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="save-testimonial-button"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Testimonial'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-2 rounded-lg border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-100 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

const AdminTestimonialsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [dupGroups, setDupGroups] = useState([]);
  const [showDupPanel, setShowDupPanel] = useState(false);

  useEffect(() => {
    fetchTestimonials();
    fetchDuplicates();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/testimonials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestimonials(response.data);
    } catch (error) {
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  const fetchDuplicates = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/testimonials/duplicates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDupGroups(r.data?.groups || []);
    } catch (_) { /* non-fatal */ }
  };

  const handleSave = async (formData) => {
    try {
      if (editingTestimonial) {
        await axios.put(`${API}/api/admin/testimonials/${editingTestimonial.testimonial_id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Testimonial updated');
      } else {
        const post = (force) =>
          axios.post(`${API}/api/admin/testimonials${force ? '?force=true' : ''}`, formData, {
            headers: { Authorization: `Bearer ${token}` }
          });
        try {
          await post(false);
          toast.success('Testimonial added');
        } catch (err) {
          const detail = err?.response?.data?.detail;
          if (err?.response?.status === 409 && detail?.code === 'duplicate_testimonial') {
            const existing = detail.existing || {};
            const proceed = window.confirm(
              `${detail.message}\n\nExisting testimonial:\n  By: ${existing.name || '—'}\n  Quote: "${(existing.quote || '').slice(0, 120)}${(existing.quote || '').length > 120 ? '…' : ''}"\n\nAdd this one anyway? (You'll have two testimonials with the same quote.)`
            );
            if (!proceed) {
              toast.message('Skipped duplicate');
              return;
            }
            await post(true);
            toast.success('Testimonial added');
          } else {
            throw err;
          }
        }
      }
      setShowForm(false);
      setEditingTestimonial(null);
      fetchTestimonials();
      fetchDuplicates();
    } catch (error) {
      toast.error('Failed to save testimonial');
    }
  };

  const handleDelete = async (testimonialId) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/testimonials/${testimonialId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Testimonial deleted');
      fetchTestimonials();
      fetchDuplicates();
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  const handleEdit = (testimonial) => {
    setEditingTestimonial(testimonial);
    setShowForm(true);
  };

  const handleToggleHeroHighlight = async (testimonial) => {
    // Optimistic mutex: client-side flip the badge instantly while the server confirms.
    // Backend enforces single-highlight mutex authoritatively.
    const turningOn = !testimonial.hero_highlighted;
    try {
      await axios.put(
        `${API}/api/admin/testimonials/${testimonial.testimonial_id}`,
        { hero_highlighted: turningOn },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(turningOn ? `Pinned "${testimonial.name}" to homepage hero` : 'Removed from homepage hero');
      fetchTestimonials();
    } catch (error) {
      toast.error('Could not update hero highlight');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingTestimonial(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <header className="bg-cyan-500 text-white py-6 px-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-black uppercase">Testimonials</h1>
            {!showForm && (
              <button
                onClick={() => setShowForm(true)}
                className="bg-white text-cyan-600 px-4 py-2 rounded-full font-bold hover:bg-cyan-50 transition-colors flex items-center gap-2"
                data-testid="add-testimonial-button"
              >
                <Plus className="w-5 h-5" />
                Add Testimonial
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <p className="text-blue-800 text-sm">
            <strong>Note:</strong> Testimonials you add here will appear on the homepage. 
            Real customer reviews from your tours will also be featured automatically if they have high ratings.
          </p>
        </div>

        {/* Form */}
        {showForm && (
          <TestimonialForm
            testimonial={editingTestimonial}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        )}

        {/* Duplicate-detection banner */}
        {dupGroups.length > 0 && (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="dup-banner">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="font-black text-amber-900">
                  {dupGroups.length} duplicate group{dupGroups.length === 1 ? '' : 's'} detected
                </div>
                <div className="text-sm text-amber-800 mt-0.5">
                  {dupGroups.reduce((sum, g) => sum + g.length - 1, 0)} testimonial{dupGroups.reduce((sum, g) => sum + g.length - 1, 0) === 1 ? '' : 's'} could be removed.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowDupPanel((v) => !v)}
                data-testid="dup-toggle"
                className="text-amber-900 hover:text-amber-950 font-bold text-sm underline"
              >
                {showDupPanel ? 'Hide' : 'Review'}
              </button>
            </div>
            {showDupPanel && (
              <div className="mt-3 space-y-3" data-testid="dup-panel">
                {dupGroups.map((group, gi) => (
                  <div key={gi} className="bg-white border border-amber-200 rounded-lg p-3">
                    <div className="text-xs uppercase tracking-wider text-amber-700 font-bold mb-2">
                      Quote (×{group.length})
                    </div>
                    <div className="text-sm text-slate-800 italic mb-3">"{group[0].quote}"</div>
                    <div className="space-y-1.5">
                      {group.map((t, idx) => (
                        <div key={t.testimonial_id} className="flex items-center justify-between gap-2 text-sm">
                          <div className="min-w-0 flex-1">
                            <span className="font-bold">{t.name}</span>
                            <span className="text-slate-500"> · {t.location || '—'}</span>
                            <span className="text-slate-400 text-xs"> · {(t.created_at || '').slice(0, 10)}</span>
                            {idx === 0 && <span className="ml-2 text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold">Original</span>}
                          </div>
                          {idx > 0 && (
                            <button
                              onClick={() => handleDelete(t.testimonial_id)}
                              data-testid={`dup-delete-${t.testimonial_id}`}
                              className="text-rose-600 hover:text-rose-800 font-bold text-xs px-2 py-1 rounded hover:bg-rose-50"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Testimonials List */}
        {testimonials.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Quote className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Testimonials Yet</h3>
            <p className="text-gray-600 mb-4">Add your first testimonial to showcase on the homepage.</p>
            {!showForm && (
              <button
                onClick={() => setShowForm(true)}
                className="bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-2 rounded-full font-bold transition-colors"
              >
                Add Testimonial
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.testimonial_id}
                className={`bg-white rounded-xl border-2 p-5 ${testimonial.hero_highlighted ? 'border-amber-400 shadow-amber-100 shadow-lg' : testimonial.featured ? 'border-cyan-200' : 'border-gray-200'}`}
                data-testid={`testimonial-card-${testimonial.testimonial_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <Quote className="w-6 h-6 text-cyan-500/50" />
                      <StarRating rating={testimonial.rating} />
                      {testimonial.featured && (
                        <span className="bg-cyan-100 text-cyan-700 text-xs font-bold px-2 py-1 rounded-full">
                          Featured
                        </span>
                      )}
                      {testimonial.hero_highlighted && (
                        <span className="bg-amber-100 text-amber-800 text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1" data-testid={`hero-badge-${testimonial.testimonial_id}`}>
                          <Pin className="w-3 h-3" />
                          Homepage Hero
                        </span>
                      )}
                    </div>
                    <p className="text-gray-700 italic mb-3">"{testimonial.quote}"</p>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-900">{testimonial.name}</span>
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-600">{testimonial.location}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleToggleHeroHighlight(testimonial)}
                      title={testimonial.hero_highlighted ? 'Remove from homepage hero' : 'Pin to homepage hero (replaces any other highlight)'}
                      className={`p-2 rounded-lg transition-all ${testimonial.hero_highlighted ? 'text-amber-600 bg-amber-50 hover:bg-amber-100' : 'text-gray-500 hover:text-amber-600 hover:bg-amber-50'}`}
                      data-testid={`pin-testimonial-${testimonial.testimonial_id}`}
                    >
                      <Pin className={`w-5 h-5 ${testimonial.hero_highlighted ? 'fill-amber-400' : ''}`} />
                    </button>
                    <button
                      onClick={() => handleEdit(testimonial)}
                      className="p-2 text-gray-500 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all"
                      data-testid="edit-testimonial-button"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(testimonial.testimonial_id)}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      data-testid="delete-testimonial-button"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminTestimonialsPage;
